import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, BookOpen, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";

const CompletionPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        const progressRes = await API.get("/journey/progress-map");
        setData(progressRes.data);
        
        // Check if all 9 steps completed — if so, check closing checkin
        if (progressRes.data.completed_count >= 9) {
          const closingRes = await API.get("/journey/closing-checkin");
          if (!closingRes.data.completed) {
            navigate("/avslutande-reflektion", { replace: true });
            return;
          }
        }
      } catch {}
      setLoading(false);
    };
    init();
  }, [navigate]);

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 500);
    const t2 = setTimeout(() => setStep(2), 1800);
    const t3 = setTimeout(() => setStep(3), 3200);
    const t4 = setTimeout(() => setStep(4), 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-xl mx-auto text-center">
          
          {step >= 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
              <Sparkles className="mx-auto text-[#A0655A] mb-6" size={40} />
              <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-4 leading-snug" data-testid="completion-title">
                Du har gått hela vägen
              </h1>
            </motion.div>
          )}

          {step >= 2 && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
              <div className="bg-white border border-[#EBE3DF] rounded-2xl p-8 mb-8 text-left">
                <p className="text-[#111111]/70 leading-relaxed mb-4">
                  Du har rest genom alla tre faserna — klarhet, frigörelse och förankring. 
                  Det är ingen liten sak. Du har visat mod, närvaro och öppenhet.
                </p>
                <p className="text-[#111111]/70 leading-relaxed mb-4">
                  Men det här är inte ett slut. Det är en ny början. Det du har lärt dig 
                  om dig själv lever vidare i hur du möter varje ny dag.
                </p>
                <p className="text-[#111111]/70 leading-relaxed italic">
                  Du är fri att vara den du alltid har varit.
                </p>
              </div>
            </motion.div>
          )}

          {step >= 3 && data && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-8">
              <p className="text-sm text-[#111111]/40 mb-4">Din resa i siffror</p>
              <div className="flex justify-center gap-8">
                <div className="text-center">
                  <p className="text-2xl font-light text-[#111111]">{data.completed_count}</p>
                  <p className="text-xs text-[#111111]/40">steg avklarade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-light text-[#111111]">{data.reflection_count}</p>
                  <p className="text-xs text-[#111111]/40">reflektioner</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-light text-[#111111]">{data.days_on_journey}</p>
                  <p className="text-xs text-[#111111]/40">dagar</p>
                </div>
              </div>
            </motion.div>
          )}

          {step >= 4 && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/min-resa')} variant="outline" className="rounded-full" data-testid="completion-journal-btn">
                <BookOpen size={16} className="mr-2" /> Läs min resa
              </Button>
              <Button onClick={() => navigate('/progress')} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full" data-testid="completion-progress-btn">
                Se min progress
              </Button>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CompletionPage;
